import { useState } from 'react';
import { T } from 'gt-react';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { BellIcon } from '@phosphor-icons/react';
import {
  useNotificationsList,
  useMarkNotificationAsRead,
  useArchiveNotification,
  useUnarchiveNotification,
  useMarkAllNotificationsAsRead,
} from '@/api/rpc/hooks/communications/useNotifications';
import { NotificationItem } from '@/components/notifications/NotificationItem';

const PAGE_SIZE = 50;

/**
 * Full-page notification history. A single vertical timeline of
 * everything the user has ever received — read, unread, and dismissed
 * (archived) — sorted newest → oldest. The "Unread only" switch narrows
 * within that. Read/archived rows visually fade to a muted background
 * (handled by NotificationItem); hovering an archived row swaps the
 * "Dismiss" action for "Restore".
 */
export function NotificationsHistoryPage() {
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [cursor, setCursor] = useState<string | undefined>(undefined);

  const { data, isLoading, isFetching } = useNotificationsList({
    limit: PAGE_SIZE,
    unreadOnly,
    // History page is "everything I've ever received" — include archived
    // rows so users can find a notification they dismissed earlier.
    filter: 'all',
    cursor,
  });

  const markAsRead = useMarkNotificationAsRead();
  const archive = useArchiveNotification();
  const unarchive = useUnarchiveNotification();
  const markAllAsRead = useMarkAllNotificationsAsRead();

  const notifications = data?.notifications ?? [];

  return (
    <div className="mx-auto w-full max-w-3xl space-y-4 p-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">
          <T context="notifications history page title">Notifications</T>
        </h1>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Switch
              id="notifications-unread-only"
              checked={unreadOnly}
              onCheckedChange={(checked) => {
                setUnreadOnly(checked);
                // New filter window — drop back to page 1 so the cursor
                // doesn't point at a row outside the new result set.
                setCursor(undefined);
              }}
            />
            <Label
              htmlFor="notifications-unread-only"
              className="cursor-pointer text-sm"
            >
              <T context="notifications filter toggle for unread only">
                Unread only
              </T>
            </Label>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => markAllAsRead.mutate({})}
            disabled={markAllAsRead.isPending}
          >
            <T context="mark all notifications as read">Mark all read</T>
          </Button>
        </div>
      </div>

      <div className="rounded-md border bg-card">
        {isLoading ? (
          <div className="space-y-2 p-3">
            {[0, 1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-14 w-full" />
            ))}
          </div>
        ) : notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 px-4 py-16 text-center">
            <BellIcon
              className="h-10 w-10 text-muted-foreground"
              weight="regular"
            />
            <div className="text-sm font-medium">
              {unreadOnly ? (
                <T context="empty inbox title when filtering by unread">
                  No unread notifications
                </T>
              ) : (
                <T context="empty inbox title">All caught up</T>
              )}
            </div>
            <div className="text-xs text-muted-foreground">
              <T context="empty inbox body">You have no notifications.</T>
            </div>
          </div>
        ) : (
          <div className="divide-y">
            {notifications.map((n) => (
              <NotificationItem
                key={n.id}
                notification={n}
                onMarkAsRead={(id) => markAsRead.mutate({ notificationId: id })}
                onArchive={(id) => archive.mutate({ notificationId: id })}
                onUnarchive={(id) => unarchive.mutate({ notificationId: id })}
              />
            ))}
          </div>
        )}
      </div>

      {data?.hasMore && data.nextCursor && (
        <div className="flex justify-center">
          <Button
            variant="outline"
            size="sm"
            disabled={isFetching}
            onClick={() => setCursor(data.nextCursor ?? undefined)}
          >
            <T context="load next page of notifications">Load more</T>
          </Button>
        </div>
      )}
    </div>
  );
}
